import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, Redirect } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Button, makeStyles, Typography } from '@popmenu/common-ui';
import { useCreateUserTasteComparisonMutation } from '~/libs/gql/mutations/eat/createUserTasteComparisonMutation.generated';
import { useNextUserTasteComparisonQuery } from '~/libs/gql/queries/eat/nextUserTasteComparisonQuery.generated';
import { screenReaderOnly } from '~/assets/jss/utils/screenReaderOnly';
import { UserTasteComparisonOutcome } from '~/libs/gql/types';
import { useEatSession } from '../../hooks/useEatSession';
import { useReturnTo } from '../../hooks/useReturnTo';
import { useSignInPath } from '../../hooks/useSignInPath';
import { formatTasteNotice } from '../ProfilePage/formatTasteTrait';
import { RefineDishCard, RefineDishCardSkeleton } from '../RefineDishCard';

// Matches User::TasteComparison::ROUND_SIZE
const ROUND_SIZE = 15;
// Long enough to read while the next pair loads. The bubble's float lasts just as long, so it fades out as it's removed.
const LEARNED_NOTICE_MS = 3000;

// The game's name heads both the page and its round progress
const useMessages = () => {
  const intl = useIntl();

  return {
    title: intl.formatMessage({ defaultMessage: 'Refine your palate' }),
  };
};

const KEY_OUTCOMES: Record<string, UserTasteComparisonOutcome> = {
  ArrowDown: UserTasteComparisonOutcome.NoPreference,
  ArrowLeft: UserTasteComparisonOutcome.Left,
  ArrowRight: UserTasteComparisonOutcome.Right,
};

// Where a pick's notice floats up from: the dish picked, or the controls between them for no preference and skip
type NoticeOrigin = 'left' | 'middle' | 'right';

const noticeOrigin = (outcome: UserTasteComparisonOutcome): NoticeOrigin => {
  switch (outcome) {
    case UserTasteComparisonOutcome.Left:
      return 'left';
    case UserTasteComparisonOutcome.Right:
      return 'right';
    default:
      return 'middle';
  }
};

// Keeps the bubble centered on its origin while it sways side to side
const bubbleTransform = (swayPx: number, risePx: number, scale = 1, rotateDeg = 0) => (
  `translate(calc(-50% + ${swayPx}px), ${-risePx}px) scale(${scale}) rotate(${rotateDeg}deg)`
);

const useStyles = makeStyles(theme => ({
  '@keyframes learnedBubbleFade': {
    '0%': { opacity: 0 },
    '10%': { opacity: 1 },
    '88%': { opacity: 1 },
    '100%': { opacity: 0 },
  },
  // Pops up from the pick, drifts upward with a gentle sway like a bubble, then pops away
  '@keyframes learnedBubbleFloat': {
    '0%': { opacity: 0, transform: bubbleTransform(0, -16, 0.6) },
    '10%': { opacity: 1, transform: bubbleTransform(-6, 0, 1.05, -2) },
    '30%': { transform: bubbleTransform(6, 16, 1, 2) },
    '50%': { transform: bubbleTransform(-6, 32, 1, -2) },
    '70%': { transform: bubbleTransform(6, 48, 1, 2) },
    '88%': { opacity: 1, transform: bubbleTransform(-3, 60, 1, -1) },
    '100%': { opacity: 0, transform: bubbleTransform(0, 72, 1.15) },
  },
  centered: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    margin: theme.spacing(3, 'auto', 0),
    maxWidth: 640,
    textAlign: 'center',
  },
  choice: {
    display: 'flex',
    flex: '1 1 320px',
    maxWidth: 460,
    // Anchors the notice bubble for a pick of this dish
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      // Each dish starts at its text plus the smallest photo, then both grow by the same share of the height left
      // over, so the photos match even when one dish's name wraps
      flex: '1 1 auto',
      maxWidth: 'none',
    },
  },
  choices: {
    alignItems: 'stretch',
    display: 'flex',
    gap: theme.spacing(3),
    justifyContent: 'center',
    [theme.breakpoints.down('xs')]: {
      flex: '1 1 auto',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(2),
      // At least the phone screen below the nav, so the photos grow to fill it. When the pair needs more room, such
      // as in landscape or with larger text, the page grows and scrolls instead. Half the page padding above and below
      // leaves more room for the photos.
      margin: theme.spacing(-2, 0),
      minHeight: `calc(100dvh - var(--eat-nav-height, 0px) - ${theme.spacing(4)}px)`,
    },
  },
  // Stays below the nav while scrolling, so the picks left in the round are always in view
  header: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    margin: theme.spacing(-1.5, 0),
    padding: theme.spacing(1.5, 0),
    position: 'sticky',
    top: 'var(--eat-nav-height, 0px)',
    zIndex: 1,
  },
  heading: {
    fontSize: '2.125rem',
    fontWeight: 600,
    lineHeight: 1.2,
    margin: 0,
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.375rem',
      textAlign: 'left',
    },
  },
  keysHint: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  // Floats over what was picked. Hidden from screen readers, which hear the notice from learnedStatus instead.
  learnedBubble: {
    '@media (prefers-reduced-motion: reduce)': {
      animation: `$learnedBubbleFade ${LEARNED_NOTICE_MS}ms linear forwards`,
      transform: 'translateX(-50%)',
    },
    animation: `$learnedBubbleFloat ${LEARNED_NOTICE_MS}ms ease-in-out forwards`,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 24,
    boxShadow: theme.shadows[3],
    fontSize: '0.875rem',
    fontWeight: 600,
    left: '50%',
    maxWidth: 'min(90%, 360px)',
    padding: theme.spacing(0.75, 2),
    pointerEvents: 'none',
    position: 'absolute',
    textAlign: 'center',
    top: '40%',
    width: 'max-content',
    zIndex: 2,
  },
  // Spills past the narrow controls column so the notice isn't squeezed. Phones put the controls in a row under the
  // dishes, so the bubble starts just above them instead of covering them.
  learnedBubbleMiddle: {
    maxWidth: 280,
    [theme.breakpoints.down('xs')]: {
      bottom: '100%',
      top: 'auto',
    },
  },
  learnedStatus: screenReaderOnly,
  muted: {
    color: theme.palette.text.secondary,
  },
  noPreference: {
    borderColor: theme.palette.text.primary,
    borderRadius: 24,
    fontWeight: 600,
    minHeight: 48,
    textTransform: 'none',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flex: 1,
      width: 'auto',
    },
  },
  // Level with the photos, well above the controls, so it reads as "this or that" rather than "or no preference"
  or: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    left: 0,
    position: 'absolute',
    right: 0,
    textAlign: 'center',
    top: '35%',
    transform: 'translateY(-50%)',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  otherChoices: {
    alignItems: 'center',
    display: 'flex',
    flex: '0 1 180px',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      flex: '0 0 auto',
      flexDirection: 'row',
      justifyContent: 'center',
      order: 1,
    },
  },
  pillButton: {
    borderColor: theme.palette.text.primary,
    borderRadius: 24,
    fontWeight: 600,
    minHeight: 48,
    padding: theme.spacing(0, 3.5),
    textTransform: 'none',
  },
  progress: {
    display: 'flex',
    flex: '0 1 420px',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  progressBar: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    height: 8,
    overflow: 'hidden',
  },
  progressFill: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
    height: '100%',
    transition: theme.transitions.create('width'),
  },
  progressLabel: {
    display: 'flex',
    fontSize: '0.8125rem',
    fontWeight: 600,
    justifyContent: 'space-between',
  },
  progressTitle: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  saveAndExit: {
    flexShrink: 0,
    fontWeight: 600,
    textTransform: 'none',
  },
  skip: {
    '&:hover': {
      textDecoration: 'underline',
    },
    color: theme.palette.text.secondary,
    fontWeight: 600,
    minHeight: 44,
    textDecoration: 'underline',
    textTransform: 'none',
  },
}));

type RoundProgressProps = {
  // Null until the next pair loads and says where the round is
  completedPicks: number | null
}

const RoundProgress = ({ completedPicks }: RoundProgressProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const msg = useMessages();
  const isComplete = completedPicks !== null && completedPicks >= ROUND_SIZE;

  return (
    <Box className={classes.progress}>
      <Box className={classes.progressLabel}>
        <span className={classes.progressTitle}>{msg.title}</span>
        <span>
          {isComplete && <FormattedMessage defaultMessage="Round complete" />}
          {!isComplete && completedPicks !== null &&
            intl.formatMessage({ defaultMessage: 'Pick {pickNumber} of {roundSize}' }, { pickNumber: completedPicks + 1, roundSize: ROUND_SIZE })}
        </span>
      </Box>
      <Box
        aria-label={intl.formatMessage({ defaultMessage: 'Round progress' })}
        aria-valuemax={ROUND_SIZE}
        aria-valuemin={0}
        aria-valuenow={completedPicks ?? 0}
        className={classes.progressBar}
        role="progressbar"
      >
        <Box className={classes.progressFill} style={{ width: `${((completedPicks ?? 0) / ROUND_SIZE) * 100}%` }} />
      </Box>
    </Box>
  );
};

type ComparisonPair = {
  leftMenuItem?: { id: number } | null
  rightMenuItem?: { id: number } | null
}

const pairKey = ({ leftMenuItem, rightMenuItem }: ComparisonPair) => `${leftMenuItem?.id}:${rightMenuItem?.id}`;

type RefineGameProps = {
  returnTo: string
}

const RefineGame = ({ returnTo }: RefineGameProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [isRoundComplete, setIsRoundComplete] = useState(false);
  const [isNextPairFailed, setIsNextPairFailed] = useState(false);
  // The last pick saved: its pair stays hidden, and its place in the round keeps the progress steady until the next pair loads
  const [savedPick, setSavedPick] = useState<{ pairKey: string, roundPickNumber: number | null } | null>(null);
  // What the last pick taught us, shown for a few seconds; most picks teach nothing new. The id restarts the bubble's
  // float when one notice replaces another.
  const [learnedNotice, setLearnedNotice] = useState<{ id: number, origin: NoticeOrigin, text: string } | null>(null);
  const { data, error, loading, refetch } = useNextUserTasteComparisonQuery({
    fetchPolicy: 'network-only',
    // Keeps the buttons disabled until the next pair arrives, so a quick second tap can't save the old pair again
    notifyOnNetworkStatusChange: true,
    ssr: false,
  });
  const [createComparison, { loading: saving }] = useCreateUserTasteComparisonMutation();

  // Apollo keeps the pair just saved until the next one loads; treat it as no pair so it's never shown or picked again
  const loadedComparison = data?.nextUserTasteComparison ?? null;
  const comparison = loadedComparison && pairKey(loadedComparison) !== savedPick?.pairKey ? loadedComparison : null;
  const left = comparison?.leftMenuItem;
  const right = comparison?.rightMenuItem;
  const isBusy = loading || saving;

  const loadNextPair = useCallback(async () => {
    setIsNextPairFailed(false);
    try {
      await refetch();
    } catch {
      setIsNextPairFailed(true);
    }
  }, [refetch]);

  // A failed save keeps the pair on screen to try again. Once a pick is saved, the pair never comes back, even when
  // the next one fails to load, so a second tap can't save it twice.
  const pick = useCallback(async (outcome: UserTasteComparisonOutcome) => {
    if (!left || !right || isBusy || isNextPairFailed) return;

    let roundPickNumber;
    try {
      const result = await createComparison({ variables: { leftMenuItemId: left.id, outcome, rightMenuItemId: right.id } });
      roundPickNumber = result.data?.createUserTasteComparison.roundPickNumber;
      setSavedPick({ pairKey: `${left.id}:${right.id}`, roundPickNumber: roundPickNumber ?? null });
      const learnedTrait = result.data?.createUserTasteComparison.learnedTrait;
      const notice = learnedTrait && formatTasteNotice(intl, learnedTrait);
      if (notice) setLearnedNotice({ id: Date.now(), origin: noticeOrigin(outcome), text: notice });
    } catch {
      enqueueSnackbar(intl.formatMessage({ defaultMessage: "We couldn't save that pick. Please try again." }), { variant: 'error' });
      return;
    }
    if (roundPickNumber === ROUND_SIZE) setIsRoundComplete(true);
    await loadNextPair();
  }, [createComparison, enqueueSnackbar, intl, isBusy, isNextPairFailed, left, loadNextPair, right]);

  useEffect(() => {
    if (!learnedNotice) return undefined;

    const timeout = setTimeout(() => setLearnedNotice(null), LEARNED_NOTICE_MS);
    return () => clearTimeout(timeout);
  }, [learnedNotice]);

  useEffect(() => {
    if (isRoundComplete) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      const outcome = KEY_OUTCOMES[event.key];
      // Holding a key repeats keydown, which would keep picking the same side on every pair that loads
      if (!outcome || event.repeat || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.target instanceof HTMLElement && event.target.closest('input, select, textarea, [contenteditable="true"]')) return;

      event.preventDefault();
      void pick(outcome);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isRoundComplete, pick]);

  let completedPicks: number | null = null;
  if (isRoundComplete) {
    completedPicks = ROUND_SIZE;
  } else if (comparison) {
    completedPicks = comparison.roundPickNumber - 1;
  } else if (savedPick?.roundPickNumber) {
    // A full round starts over at pick 1
    completedPicks = savedPick.roundPickNumber % ROUND_SIZE;
  }
  const saveAndExit = (
    <Button className={classes.saveAndExit} component={Link} role="link" to={returnTo} variant="text">
      <FormattedMessage defaultMessage="Save and exit" />
    </Button>
  );

  let content: React.ReactNode;
  if (isRoundComplete) {
    content = (
      <Box className={classes.centered}>
        <Typography className={classes.heading} component="h1">
          <FormattedMessage defaultMessage="Round complete" />
        </Typography>
        <Typography className={classes.muted}>
          <FormattedMessage
            defaultMessage="Thanks! Your taste profile has improved from these picks."
          />
        </Typography>
        <Box display="flex" flexWrap="wrap" gridGap={12} justifyContent="center">
          <Button className={classes.pillButton} color="primary" component={Link} role="link" to={returnTo} variant="contained">
            <FormattedMessage defaultMessage="Done" />
          </Button>
          <Button className={classes.pillButton} onClick={() => setIsRoundComplete(false)} variant="outlined">
            <FormattedMessage defaultMessage="Keep refining" />
          </Button>
        </Box>
      </Box>
    );
  } else if (isNextPairFailed || (error && !comparison)) {
    content = (
      <Box className={classes.centered}>
        <Typography className={classes.heading} component="h1">
          <FormattedMessage defaultMessage="We couldn't load the next pair" />
        </Typography>
        <Typography className={classes.muted}>
          <FormattedMessage defaultMessage="Your picks so far are saved." />
        </Typography>
        <Button className={classes.pillButton} disabled={loading} onClick={() => { void loadNextPair(); }} variant="outlined">
          <FormattedMessage defaultMessage="Try again" />
        </Button>
      </Box>
    );
  } else if (!loading && !loadedComparison) {
    content = (
      <Box className={classes.centered}>
        <Typography className={classes.heading} component="h1">
          <FormattedMessage defaultMessage="That's every pair for now" />
        </Typography>
        <Typography className={classes.muted}>
          <FormattedMessage
            defaultMessage="We're adding dishes all the time. Check back soon for more."
          />
        </Typography>
      </Box>
    );
  } else {
    // One layout whether the pair has loaded or not, so only the cards change between picks
    const isPairLoading = !left || !right;
    const isDisabled = isBusy || isPairLoading;
    const learnedBubble = (origin: NoticeOrigin) => learnedNotice?.origin === origin && (
      <Box
        aria-hidden
        className={origin === 'middle' ? `${classes.learnedBubble} ${classes.learnedBubbleMiddle}` : classes.learnedBubble}
        key={learnedNotice.id}
      >
        {learnedNotice.text}
      </Box>
    );
    content = (
      <React.Fragment>
        <Typography className={classes.heading} component="h1">
          <FormattedMessage defaultMessage="Which dish would you rather try?" />
        </Typography>
        <Box aria-busy={isPairLoading} className={classes.choices}>
          <Box className={classes.choice}>
            {left ?
              <RefineDishCard disabled={isDisabled} dish={left} onPick={() => { void pick(UserTasteComparisonOutcome.Left); }} /> :
              <RefineDishCardSkeleton />}
            {learnedBubble('left')}
          </Box>
          <Box className={classes.otherChoices}>
            <Typography className={classes.or} component="span">
              <FormattedMessage defaultMessage="or" />
            </Typography>
            <Button className={classes.noPreference} disabled={isDisabled} onClick={() => { void pick(UserTasteComparisonOutcome.NoPreference); }} variant="outlined">
              <FormattedMessage defaultMessage="No preference" />
            </Button>
            <Button className={classes.skip} disabled={isDisabled} onClick={() => { void pick(UserTasteComparisonOutcome.Skip); }} variant="text">
              <FormattedMessage defaultMessage="Skip this pair" />
            </Button>
            <Typography className={classes.keysHint} component="span">
              <FormattedMessage defaultMessage="Keys: ← left · → right · ↓ no preference" />
            </Typography>
            {learnedBubble('middle')}
          </Box>
          <Box className={classes.choice}>
            {right ?
              <RefineDishCard disabled={isDisabled} dish={right} onPick={() => { void pick(UserTasteComparisonOutcome.Right); }} /> :
              <RefineDishCardSkeleton />}
            {learnedBubble('right')}
          </Box>
        </Box>
      </React.Fragment>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <RoundProgress completedPicks={completedPicks} />
        {/* Always rendered, so screen readers announce each notice as it appears */}
        <Box className={classes.learnedStatus} role="status">{learnedNotice?.text}</Box>
        {saveAndExit}
      </Box>
      {content}
    </Box>
  );
};

// "Which dish would you rather try?": rounds of picks that teach the taste profile. Signed-in only.
export const RefinePage = () => {
  const msg = useMessages();
  const returnTo = useReturnTo();
  const signInPath = useSignInPath();
  const { loading, user } = useEatSession();

  if (!loading && !user) return <Redirect to={signInPath} />;

  return (
    <React.Fragment>
      <Helmet title={msg.title}>
        <meta name="robots" content="noindex" />
      </Helmet>
      {user && <RefineGame returnTo={returnTo} />}
    </React.Fragment>
  );
};
